/**
 * SelectSocietyModal: Modal for selecting city and society and saving to user profile.
 * Used when the user has no society set (e.g. on /posts/create). On success calls onSuccess
 * so the parent can refresh user data and continue.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { societyService } from '../../services/societyService';
import { authService } from '../../services/api';
import SearchableSelect from './SearchableSelect';

const SelectSocietyModal = ({ isOpen, onClose, onSuccess }) => {
  const [city, setCity] = useState('');
  const [societyId, setSocietyId] = useState('');
  const [cities, setCities] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [loadingSocieties, setLoadingSocieties] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadCities = useCallback(async () => {
    setLoadingCities(true);
    setError(null);
    try {
      const response = await societyService.getCities();
      const list = response.cities || [];
      setCities(Array.isArray(list) ? list.sort((a, b) => a.localeCompare(b)) : []);
    } catch (err) {
      console.error('Error loading cities:', err);
      setError('Failed to load cities.');
    } finally {
      setLoadingCities(false);
    }
  }, []);

  const loadSocieties = useCallback(async (cityName) => {
    if (!cityName) {
      setSocieties([]);
      setSocietyId('');
      return;
    }
    setLoadingSocieties(true);
    setError(null);
    try {
      const list = await societyService.getSocietiesForCityAll(cityName);
      const sorted = Array.isArray(list) ? list.sort((a, b) => (a.name || '').localeCompare(b.name || '')) : [];
      setSocieties(sorted);
      setSocietyId('');
    } catch (err) {
      console.error('Error loading societies:', err);
      setError('Failed to load societies for this city.');
      setSocieties([]);
    } finally {
      setLoadingSocieties(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setCity('');
      setSocietyId('');
      setSocieties([]);
      setError(null);
      loadCities();
    }
  }, [isOpen, loadCities]);

  useEffect(() => {
    if (isOpen && city) {
      loadSocieties(city);
    } else if (isOpen && !city) {
      setSocieties([]);
      setSocietyId('');
    }
  }, [isOpen, city, loadSocieties]);

  const handleSetSociety = async () => {
    if (!societyId) {
      setError('Please select a society.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await authService.updateUserProfile({ society: parseInt(societyId, 10) });
      if (typeof onSuccess === 'function') {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error updating profile with society:', err);
      setError(err.message || 'Failed to set society. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      onClose();
    }
  };

  const societyOptions = useMemo(
    () =>
      societies.map((s) => ({
        value: String(s.id),
        label: s.name,
      })),
    [societies],
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Select your city and society</h3>
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-4 space-y-4">
          <p className="text-sm text-gray-600">
            You need a society set on your profile to create posts. Select your city and society below.
          </p>

          <SearchableSelect
            id="select-society-city"
            label="City *"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            options={cities}
            searchable
            disabled={loadingCities}
            loading={loadingCities}
            loadingLabel="Loading cities…"
            emptyOption={{ value: '', label: 'Select city' }}
            searchPlaceholder="Search cities…"
            required
          />

          <SearchableSelect
            id="select-society-society"
            label="Society *"
            value={societyId}
            onChange={(e) => setSocietyId(e.target.value)}
            options={societyOptions}
            searchable
            disabled={!city || loadingSocieties}
            loading={loadingSocieties}
            loadingLabel="Loading societies…"
            emptyOption={{
              value: '',
              label: !city
                ? 'Select city first'
                : societies.length === 0
                  ? 'No societies in this city'
                  : 'Select society',
            }}
            searchPlaceholder="Search societies…"
            required
          />

          {error && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-4 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSetSociety}
            disabled={saving || !societyId}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Set and continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectSocietyModal;
